import { useEffect, useState } from 'react';
import { useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import ShortUniqueId from 'short-unique-id';
import { RootState } from '../../../redux';
import { Box } from '@mui/material';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import {
  addCalculationField,
  addResultantDatatypeInCalculationFlow,
  deleteField,
  setSource,
  updateSourceAggregation,
} from '../../../redux/Calculations/CalculationsActions';
import { isFlowAlreadyPresentInStep, isFlowFilledProperly } from '../utils';
import { ca } from 'date-fns/locale';

interface NumberDropZoneProps {
  children: React.ReactNode;
  propKey: string;
  flow: string;
  sourceLength?: number;
  identifier?: string;
  disableDrop?: boolean;
  sx?: React.CSSProperties;
}
function NumberDropZone({
  children,
  sx,
  disableDrop,
  flow,
  sourceLength,
  propKey,
}: NumberDropZoneProps) {
  const [showBorder, setShowBorder] = useState(false);

  const uid = new ShortUniqueId({ length: 4 });
  const _calculations = useSelector((state: RootState) => state.calculations);
  const dispatch = useDispatch();

  const [alert, setAlert] = useState<any>(null);
  const currFlow =
    _calculations?.properties[propKey]?.currentCalculationSession?.calculationInfo.flows;
  const activeFlow = _calculations.properties[propKey]?.currentCalculationSession?.activeFlow!;
  const isDropNotAllowed = (draggedFlowName: string, activeFlowName: string): boolean => {
    return draggedFlowName === activeFlowName;
  };

  const [{ isOver }, drop] = useDrop({
    accept: ['card', 'calculationSelected'],
    hover: (item: any, monitor) => {
      if (monitor.isOver({ shallow: true }) && !showBorder) {
        setShowBorder(true);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop: (item: any, monitor) => {
      if (disableDrop) {
        setAlert({
          severity: 'warning',
          message:
            'This calculation is being used in another flow. Please remove the dependency before deleting.',
        });
        return;
      }
      setShowBorder(false);
      const calculationInfo =
        _calculations.properties[propKey]?.currentCalculationSession?.calculationInfo;
      if (!calculationInfo || !currFlow) return;

      if (isDropNotAllowed(item.uid, activeFlow)) {
        setAlert({
          severity: 'warning',
          message: 'Same flow can not be dropped in itself',
        });
        return;
      }
      if (item.type === 'calculationSelected') {
        const isAgggregated =
          _calculations.properties[propKey]?.currentCalculationSession?.flowAggregations[
            activeFlow
          ];
        if (isAgggregated === null || isAgggregated === undefined) {
          setAlert({
            severity: 'warning',
            message: 'Please select an aggregation type for this flow before dropping a source',
          });
        }
        const droppedItemAggregationType = item.fieldData.isAggregated;
        if (
          (isAgggregated && !droppedItemAggregationType) ||
          (droppedItemAggregationType && !isAgggregated)
        ) {
          setAlert({
            severity: 'warning',
            message: 'Cannot drop a non-aggregated flow into an aggregated flow and vice versa',
          });
          return;
        }
      }
      if (item.type === 'calculationSelected') {
        if (!isFlowFilledProperly(calculationInfo, item.uid)) {
          setAlert({
            severity: 'warning',
            message: `Please ensure at least one field or flow is present and all conditions are met for flow ${item.name} before dropping.`,
          });
          return;
        }
        if (isFlowAlreadyPresentInStep(calculationInfo, item.uid, activeFlow, 0, 'source')) {
          const droppedFlowName =
            _calculations.properties[propKey]?.currentCalculationSession?.flowDisplayNames[
              item.uid
            ];
          const parentFlowName =
            _calculations.properties[propKey]?.currentCalculationSession?.flowDisplayNames[
              activeFlow
            ];
          setAlert({
            severity: 'warning',
            message: `${droppedFlowName} is already present inside ${parentFlowName}.`,
          });
          return;
        }
      }
      if (item.type === 'card') {
        if (!item.fieldData) return;
        if (!['integer', 'decimal'].includes(item.fieldData.dataType)) {
          setAlert({
            severity: 'warning',
            message: `Only fields with integer or decimal datatype can be added as source in Number calculations.`,
          });
          return;
        }
        const fieldId = uid();
        const source = calculationInfo.flows[activeFlow][0].source;
        dispatch(
          addCalculationField(propKey, fieldId, {
            tableId: item.fieldData.tableId,
            fieldName: item.fieldData.fieldname,
            displayName: item.fieldData.displayname,
            dataType: item.fieldData.dataType,
          }),
        );
        dispatch(
          addResultantDatatypeInCalculationFlow(
            { uid: activeFlow, flow: activeFlow },
            propKey,
            'decimal',
            0,
          ),
        );
        dispatch(updateSourceAggregation(propKey, activeFlow, 0, 0, 'sum'));
        dispatch(setSource(propKey, activeFlow, 0, fieldId, 'field', 0));
        dispatch(setSource(propKey, activeFlow, 0, source[1]??2, 'decimal', 1));
        dispatch(deleteField(propKey, source[0]));
      } else if (item.type === 'calculationSelected') {
        const currentCalculationSession =
          _calculations.properties[propKey]?.currentCalculationSession;

        const activeConditionIndex = currentCalculationSession?.activeCondition;

        if (activeConditionIndex !== 0 && !activeConditionIndex) return;

        const flowResultantDatatype =
          currentCalculationSession?.calculationInfo?.flows[item.uid][0]?.resultantDatatype;

        if (!flowResultantDatatype) return;
        if (!['integer', 'decimal'].includes(flowResultantDatatype)) {
          setAlert({
            severity: 'warning',
            message: `Only flows with integer or decimal datatype can be added as source in Number calculations.`,
          });
          return;
        }
         dispatch(
           addResultantDatatypeInCalculationFlow(
             { uid: activeFlow, flow: activeFlow },
             propKey,
             'decimal',
             0,
           ),
         );
        const source = calculationInfo.flows[activeFlow][0].source;
        dispatch(setSource(propKey, activeFlow, 0, item.uid, 'flow', 0));
        dispatch(setSource(propKey, activeFlow, 0, source[1]??2, 'decimal', 1));
      }
    },
  });
  useEffect(() => {
    if (!isOver) {
      setShowBorder(false);
    }
  }, [isOver]);
  return (
    <>
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      <Box
        ref={drop}
        sx={{
          cursor: 'pointer',
          textAlign: 'left',
          paddingBottom: '10px',
          paddingTop: '5px',
          borderBottom: '1px solid  #999999',
          height: 'auto',
          marginBottom: '0.5rem',
          border: '1px solid transparent',
          flexGrow: 1,
          ...sx,
        }}
        className={`dropzone ${showBorder ? 'dropzone-highlighted' : ''}`}
      >
        {children}
      </Box>
    </>
  );
}

export default NumberDropZone;
